/**
 * Serviço de autenticação para comunicação com a API
 */

import { API_BASE_URL } from "@/lib/apiConfig";

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  success: boolean;
  message?: string;
  data?: {
    user?: {
      id: string;
      name: string;
      email: string;
    };
    token?: string;
  };
  error?: {
    message: string;
    code?: string;
  };
}

export interface ValidateTokenResponse {
  success: boolean;
  message?: string;
  data?: {
    user?: {
      id: string;
      name: string;
      email: string;
    };
    valid: boolean;
  };
  error?: {
    message: string;
    code?: string;
  };
}

export interface LogoutResponse {
  success: boolean;
  message?: string;
  error?: {
    message: string;
    code?: string;
  };
}

export interface ChangePasswordRequest {
  current_password: string;
  new_password: string;
}

export interface ChangePasswordResponse {
  success: boolean;
  message?: string;
  error?: {
    message: string;
    code?: string;
  };
}

export interface ApiError {
  message: string;
  code?: string;
  status?: number;
}

/**
 * Realiza login na API
 */
export async function loginUser(
  credentials: LoginRequest
): Promise<LoginResponse> {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(credentials),
    });

    const data = await response.json();

    if (!response.ok) {
      // Retorna a resposta da API mesmo em caso de erro para manter o formato original
      return data;
    }

    return data;
  } catch (error) {
    if (error instanceof TypeError && error.message.includes("fetch")) {
      throw {
        message:
          "Erro de conexão com o servidor. Verifique se a API está rodando.",
        code: "CONNECTION_ERROR",
      } as ApiError;
    }

    throw error as ApiError;
  }
}

/**
 * Valida o token com a API
 */
export async function validateToken(
  token: string
): Promise<ValidateTokenResponse> {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/validate`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    const data = await response.json();

    if (!response.ok) {
      // Retorna a resposta da API mesmo em caso de erro para manter o formato original
      return data;
    }

    return data;
  } catch (error) {
    // Em caso de erro de rede, retorna resposta de erro em vez de lançar exceção
    if (error instanceof TypeError && error.message.includes("fetch")) {
      return {
        success: false,
        error: {
          message:
            "Erro de conexão com o servidor. Verifique se a API está rodando.",
          code: "CONNECTION_ERROR",
        },
      } as ValidateTokenResponse;
    }

    // Para outros erros, também retorna resposta de erro em vez de lançar exceção
    return {
      success: false,
      error: {
        message: error instanceof Error ? error.message : "Erro desconhecido",
        code: "VALIDATION_ERROR",
      },
    } as ValidateTokenResponse;
  }
}

/**
 * Realiza logout na API
 */
export async function logoutUser(token: string): Promise<LogoutResponse> {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/logout`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    const data = await response.json();

    if (!response.ok) {
      // Retorna a resposta da API mesmo em caso de erro para manter o formato original
      return data;
    }

    return data;
  } catch (error) {
    if (error instanceof TypeError && error.message.includes("fetch")) {
      throw {
        message:
          "Erro de conexão com o servidor. Verifique se a API está rodando.",
        code: "CONNECTION_ERROR",
      } as ApiError;
    }

    throw error as ApiError;
  }
}

/**
 * Altera a senha do usuário logado
 */
export async function changePassword(
  token: string,
  passwords: ChangePasswordRequest
): Promise<ChangePasswordResponse> {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/change-password`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(passwords),
    });

    const data = await response.json();

    if (!response.ok) {
      return data;
    }

    return data;
  } catch (error) {
    if (error instanceof TypeError && error.message.includes("fetch")) {
      throw {
        message:
          "Erro de conexão com o servidor. Verifique se a API está rodando.",
        code: "CONNECTION_ERROR",
      } as ApiError;
    }

    throw error as ApiError;
  }
}

export async function requestPasswordRecovery(
  email: string
): Promise<ForgotPasswordResponse> {
  try {
    const response = await fetch(`${API_BASE_URL}/email/password-recovery`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email }),
    });

    const data = await response.json();

    if (!response.ok) {
      return data as ForgotPasswordResponse;
    }

    return data as ForgotPasswordResponse;
  } catch (error) {
    if (error instanceof TypeError && error.message.includes("fetch")) {
      throw {
        message:
          "Erro de conexão com o servidor. Verifique se a API está rodando.",
        code: "CONNECTION_ERROR",
      } as ApiError;
    }

    throw error as ApiError;
  }
}

/**
 * Verifica se a API está disponível
 */
export async function checkApiHealth(): Promise<boolean> {
  try {
    const response = await fetch(`${API_BASE_URL}/health`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    return response.ok;
  } catch {
    return false;
  }
}


export interface RecoveryVerifyResponse {
  success: boolean;
  message?: string;
  error?: {
    message: string;
    code?: string;
  };
}

export interface PasswordResetResponse {
  success: boolean;
  message?: string;
  error?: {
    message: string;
    code?: string;
  };
}

/**
 * Verifica a validade de um token de recuperação de senha
 */
export async function verifyRecoveryToken(token: string): Promise<RecoveryVerifyResponse> {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/recovery/verify`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token }),
    });
    const data = await response.json();
    if (!response.ok) return data as RecoveryVerifyResponse;
    return data as RecoveryVerifyResponse;
  } catch (error) {
    if (error instanceof TypeError && error.message.includes("fetch")) {
      throw {
        message: "Erro de conexão com o servidor. Verifique se a API está rodando.",
        code: "CONNECTION_ERROR",
      } as ApiError;
    }
    throw error as ApiError;
  }
}

/**
 * Redefine a senha usando um token de recuperação válido
 */
export async function resetPasswordWithToken(token: string, newPassword: string): Promise<PasswordResetResponse> {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/recovery/reset`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token, new_password: newPassword }),
    });
    const data = await response.json();
    if (!response.ok) return data as PasswordResetResponse;
    return data as PasswordResetResponse;
  } catch (error) {
    if (error instanceof TypeError && error.message.includes("fetch")) {
      throw {
        message: "Erro de conexão com o servidor. Verifique se a API está rodando.",
        code: "CONNECTION_ERROR",
      } as ApiError;
    }
    throw error as ApiError;
  }
}

export interface WelcomeVerifyResponse {
  success: boolean;
  message?: string;
  data?: {
    id: string;
    name: string;
    email: string;
    email_verified: boolean;
    password_already_set?: boolean;
  };
  error?: {
    message: string;
    code?: string;
  };
}

export interface WelcomeCompleteResponse {
  success: boolean;
  message?: string;
  data?: {
    user?: Record<string, unknown>;
    token?: string;
    expires_in?: string;
  };
  error?:
    | string
    | {
        message?: string;
        code?: string;
      };
  field?: string;
}

export async function verifyWelcomeToken(
  token: string
): Promise<WelcomeVerifyResponse> {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/welcome/verify`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token }),
    });
    const data = await response.json();
    if (!response.ok) return data as WelcomeVerifyResponse;
    return data as WelcomeVerifyResponse;
  } catch (error) {
    if (error instanceof TypeError && error.message.includes("fetch")) {
      throw {
        message:
          "Erro de conexão com o servidor. Verifique se a API está rodando.",
        code: "CONNECTION_ERROR",
      } as ApiError;
    }
    throw error as ApiError;
  }
}

export async function completeWelcomeSetup(
  token: string,
  newPassword: string
): Promise<WelcomeCompleteResponse> {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/welcome/complete`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token, new_password: newPassword }),
    });
    const data = await response.json();
    if (!response.ok) {
      if (typeof data?.error === "string" && data?.message) {
        return {
          success: false,
          message: data.message,
          field: data.field,
          error: {
            code: data.error,
            message: data.message,
          },
        };
      }
      return data as WelcomeCompleteResponse;
    }
    return data as WelcomeCompleteResponse;
  } catch (error) {
    if (error instanceof TypeError && error.message.includes("fetch")) {
      throw {
        message:
          "Erro de conexão com o servidor. Verifique se a API está rodando.",
        code: "CONNECTION_ERROR",
      } as ApiError;
    }
    throw error as ApiError;
  }
}
